/**
=========================================================
* Trust Cargo - Orders (the Follow up sheet)
=========================================================

* Reads the v2 database, not the legacy one.
*
* This page used to call /api/orders, which returns {"orders":[]} - the
* legacy table is empty, so the screen was blank no matter what. Orders now
* live in v2 (589 of them, imported from the workbook), and that is also
* where the create dialog writes, so a new order shows up here immediately.
*
* Money columns are derived from the ledgers, never typed - Deposit and
* payment are SUMs, Balance is final_amount minus what was paid.
*
* Deposit counts both of them: what the customer put down with us
* (`payments`) and what we put down with the supplier for the same order
* (`supplier_payments`). The supplier screen is the only place a deposit can
* be entered, so counting one ledger left that money off the sheet it was
* entered against.
*/

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import axios from "axios";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import {
  FilterSelect,
  FilterDates,
  optionsFromRows,
  matches,
  inDateRange,
} from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import AddDialog from "components/AddDialog";
import OrderPhoto from "components/OrderPhoto";
import OrderStatusDialog from "components/OrderStatusDialog";
import RowActions from "components/RowActions";
import ensureSupplierForShopCode from "utils/suppliers";
import { boardAction } from "utils/board";
import fetchFirstAmounts from "utils/orderAmounts";
import PAY_TYPES, { payTypeOf } from "utils/payType";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api/v2";

const money = (v) =>
  v === null || v === undefined || v === "" ? "-" : Number(v).toLocaleString("en-US");

/**
 * What was expected to be owed before the amount was agreed: the quoted
 * amount less everything already paid on the order - the deposit and the
 * payments, ours to the supplier as well as the customer's to us.
 *
 * The server works it out, because netting off what we paid the supplier
 * needs figures this list does not carry, and sends null for an order that
 * was never quoted. The subtraction below is only for a server that does
 * not send it yet, where the deposit is all there is to go on.
 */
const expectedBalance = (o) => {
  if (o.expected_balance !== undefined) return o.expected_balance;

  return Number(o.first_amount) > 0 ? Number(o.first_amount) - (Number(o.deposit) || 0) : null;
};

/**
 * Everything paid on an order: the deposit and the payments together, ours
 * to the supplier included - what the row's own two columns add up to, and
 * what BALANCE is measured against.
 *
 * `paid` is the customer's side alone. It is the whole story only on a
 * server that does not send `payment` yet, where it is also all there is.
 */
const paidOn = (o) =>
  o.payment === undefined
    ? Number(o.paid) || 0
    : (Number(o.deposit) || 0) + (Number(o.payment) || 0);

/**
 * Whether an order carries a given status.
 *
 * Compared loosely on the name, unlike the Status filter, because a tab
 * pinned to one status names it in the code: the statuses are rows the
 * office maintains, and LOADED being renamed "Loaded" one afternoon must
 * not quietly empty the tab that exists to show them.
 */
const isStatus = (order, name) =>
  String(order.status ?? "")
    .trim()
    .toLowerCase() === String(name).trim().toLowerCase();

/**
 * The orders table.
 *
 * `lockedStatus` makes it one status's screen - the Loaded tab is this page
 * pinned to LOADED. The status filter goes with it, because a tab that says
 * Loaded and a dropdown that can turn it into something else are two
 * different answers to the same question.
 */
function Orders({ lockedStatus, title }) {
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // the order whose status is being changed, from the row's own button
  const [statusFor, setStatusFor] = useState(null);
  // ids ticked in the table, kept as a Set for O(1) lookups while rendering
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  // column filters, all applied on top of the free-text search
  const [status, setStatus] = useState("");
  // every status the system has, for the bulk change
  const [statuses, setStatuses] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [container, setContainer] = useState("");
  const [customer, setCustomer] = useState("");
  const [settled, setSettled] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      // the endpoint defaults to a small page; ask for the lot and let the
      // client-side pager and search work across all of it
      const res = await fetch(`${API}/orders?limit=2000`);
      const json = await res.json();
      const rows = Array.isArray(json) ? json : [];

      /**
       * first_amount drives the Expected Balance column but is not on the
       * list response, so it is recovered per order until the server sends
       * it. Orders that already carry it cost nothing.
       */
      const firstById = await fetchFirstAmounts(rows);

      // null rather than undefined, so the column can tell "not known" from
      // a genuine zero
      const firstFor = (o) => {
        if (o.first_amount !== undefined && o.first_amount !== null) return Number(o.first_amount);
        return firstById.has(o.id) ? firstById.get(o.id) : null;
      };

      setOrders(rows.map((o) => ({ ...o, first_amount: firstFor(o) })));
    } catch (e) {
      setOrders([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();

    // the statuses are a short fixed list; a failure here only costs the
    // bulk control, so it must not disturb the page
    fetch(`${API}/order-options`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setStatuses(Array.isArray(d?.statuses) ? d.statuses : []))
      .catch(() => setStatuses([]));
  }, []);

  /**
   * Column filters first, then the free-text search over what is left, so
   * the search box narrows the visible tab rather than the whole table.
   */
  const pool = lockedStatus ? orders.filter((o) => isStatus(o, lockedStatus)) : orders;

  const narrowed = pool.filter((o) => {
    if (!matches(o.status, status)) return false;
    if (!matches(o.container, container)) return false;
    if (!matches(o.customer, customer)) return false;
    if (!inDateRange(o.date, from, to)) return false;

    if (settled === "unpaid" && !(Number(o.balance) > 0)) return false;
    if (settled === "settled" && Number(o.balance) > 0) return false;
    if (settled === "overpaid" && !(Number(o.balance) < 0)) return false;

    return true;
  });

  const filteredRows = filterRows(narrowed, query);
  const pager = usePagination(filteredRows);

  // totals of what is on screen, so a filtered view still answers "how much"
  const shown = filteredRows.reduce(
    (acc, r) => ({
      charged: acc.charged + (Number(r.charged) || 0),
      paid: acc.paid + paidOn(r),
      balance: acc.balance + (Number(r.balance) || 0),
    }),
    { charged: 0, paid: 0, balance: 0 }
  );

  const cell = { fontSize: 14, whiteSpace: "nowrap" };

  // select-all applies to the CURRENT page only - ticking a header box and
  // silently selecting 589 hidden rows is how people delete things by mistake
  const pageIds = pager.pageRows.map((o) => o.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  /**
   * Copy an order. The server assigns the next code and the customer's next
   * shop code, and deliberately does not copy the payments - that money
   * belongs to the original order.
   */
  const duplicateOne = async (o) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/orders/${o.id}/duplicate`);

      // the copy gets a new shop code, so it needs its supplier too
      const newCode = res?.data?.order?.shop_code;
      if (newCode) await ensureSupplierForShopCode(newCode);

      await fetchData();
      // eslint-disable-next-line no-alert
      window.alert(res?.data?.message || "Duplicated");
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not duplicate");
    }
    setBusy(false);
  };

  const deleteOne = async (o) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete order ${o.shop_code} (code ${o.code})?`)) return;

    setBusy(true);
    try {
      await axios.delete(`${API}/orders/${o.id}`);
      await fetchData();
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(o.id);
        return next;
      });
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
    setBusy(false);
  };

  /**
   * Set one status on everything ticked.
   *
   * Confirmed first: it is one click over an arbitrary number of orders and
   * there is no undo, which is the same reason the delete asks.
   */
  const applyStatus = async () => {
    const ids = [...selected];
    const chosen = statuses.find((s) => String(s.id) === String(bulkStatus));
    if (!chosen || ids.length === 0) return;

    // eslint-disable-next-line no-alert
    if (!window.confirm(`Set ${ids.length} order(s) to ${chosen.name}?`)) return;

    setBusy(true);
    try {
      await axios.post(`${API}/orders/bulk-status`, { ids, status_id: chosen.id });
      setSelected(new Set());
      setBulkStatus("");
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not change the status");
    }
    setBusy(false);
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${ids.length} order(s)?`)) return;

    setBusy(true);
    try {
      await axios.post(`${API}/orders/bulk-delete`, { ids });
      setSelected(new Set());
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
    setBusy(false);
  };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <Card>
          {/* title, search, totals, count and the button - one row */}
          <FilterBar
            title={title}
            value={query}
            onChange={setQuery}
            count={filteredRows.length}
            total={pool.length}
            placeholder="Search by shop code, customer or container..."
            actions={
              <Button variant="contained" color="primary" onClick={() => setAdding(true)}>
                Add Order
              </Button>
            }
          >
            {/* the choices come from the rows themselves, so the list can
                never offer a status or container that does not exist */}
            {lockedStatus ? null : (
              <FilterSelect
                label="Status"
                icon="flag"
                value={status}
                onChange={setStatus}
                options={optionsFromRows(orders, "status")}
              />
            )}
            <FilterSelect
              label="Container"
              icon="inventory_2"
              value={container}
              onChange={setContainer}
              options={optionsFromRows(orders, "container")}
            />
            <FilterSelect
              label="Customer"
              icon="person"
              value={customer}
              onChange={setCustomer}
              options={optionsFromRows(orders, "customer")}
            />
            <FilterSelect
              label="Money"
              icon="payments"
              value={settled}
              onChange={setSettled}
              anyLabel="All"
              options={[
                { value: "unpaid", label: "Outstanding" },
                { value: "settled", label: "Settled" },
                { value: "overpaid", label: "Overpaid" },
              ]}
            />
            <FilterDates label="Ordered" from={from} to={to} onFrom={setFrom} onTo={setTo} />

            <SuiTypography variant="caption" sx={{ opacity: 0.75, whiteSpace: "nowrap" }}>
              {`Charged ${money(shown.charged)} · Paid ${money(shown.paid)} · Balance ${money(
                shown.balance
              )}`}
            </SuiTypography>

            {/* what the tinted rows mean. A colour nobody can look up is a
                puzzle rather than information, and the key is two swatches */}
            <SuiBox display="flex" alignItems="center" gap={1.5} sx={{ whiteSpace: "nowrap" }}>
              {PAY_TYPES.map((p) => (
                <SuiBox key={p.value} display="flex" alignItems="center" gap={0.75}>
                  <SuiBox
                    sx={({ palette, functions: { rgba } }) => ({
                      width: 14,
                      height: 14,
                      borderRadius: "3px",
                      backgroundColor: rgba(palette[p.tone].main, 0.25),
                      border: `1px solid ${rgba(palette[p.tone].main, 0.7)}`,
                    })}
                  />
                  <SuiTypography variant="caption" sx={{ opacity: 0.75 }}>
                    {p.label}
                  </SuiTypography>
                </SuiBox>
              ))}
            </SuiBox>
          </FilterBar>

          {/* only appears once something is ticked, so it never takes up
              space during normal browsing */}
          {selected.size > 0 && (
            <SuiBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1.5}
              mx={3}
              mb={2}
              px={2}
              py={1.25}
              sx={({ palette, borders, functions: { rgba } }) => ({
                borderRadius: borders.borderRadius.md,
                backgroundColor: rgba(palette.primary.main, 0.12),
                border: `1px solid ${rgba(palette.primary.main, 0.35)}`,
              })}
            >
              <SuiTypography variant="button" fontWeight="bold">
                {`${selected.size} selected`}
              </SuiTypography>

              <SuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {/* the status change sits with the other things you can do to
                    a selection, rather than in a menu of its own */}
                <TextField
                  select
                  size="small"
                  label="Set status"
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  disabled={busy || statuses.length === 0}
                  sx={{ minWidth: 168, backgroundColor: "white", borderRadius: 1 }}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  {/* a blank first entry would read as a status */}
                  <option value="">Choose...</option>
                  {statuses.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </TextField>

                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={applyStatus}
                  disabled={busy || !bulkStatus}
                >
                  Apply
                </Button>

                <Button size="small" onClick={() => setSelected(new Set())} disabled={busy}>
                  Clear selection
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={deleteSelected}
                  disabled={busy}
                >
                  Delete selected
                </Button>
              </SuiBox>
            </SuiBox>
          )}

          {loading ? (
            <CargoLoader label="Loading orders" />
          ) : (
            <>
              <SuiBox sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 1280 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" padding="checkbox">
                        <Checkbox
                          checked={allOnPageSelected}
                          indeterminate={
                            !allOnPageSelected && pageIds.some((id) => selected.has(id))
                          }
                          onChange={togglePage}
                        />
                      </TableCell>
                      <IdHead />
                      <TableCell align="center">CODE</TableCell>
                      <TableCell align="center">shop code</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Order Date</TableCell>
                      <TableCell align="center">Type of goods</TableCell>
                      <TableCell align="center">CON NO</TableCell>
                      <TableCell align="center">STATUS</TableCell>
                      <TableCell align="center">Final Amount</TableCell>
                      <TableCell align="center">Deposit</TableCell>
                      <TableCell align="center">payment</TableCell>
                      {/* the quoted figure first, then the agreed one */}
                      <TableCell align="center">Expected Balance</TableCell>
                      <TableCell align="center">Balance</TableCell>
                      <TableCell align="center">Operations</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pager.pageRows.map((o) => (
                      /**
                       * The row is tinted by who settles with the supplier -
                       * the same two colours as the tabs on the order form.
                       * A tint rather than a column: it is true of the whole
                       * row, and the table is wide enough already.
                       *
                       * An order with no answer yet keeps the plain row, so
                       * the colour always means something was chosen.
                       */
                      <TableRow
                        key={o.id}
                        selected={selected.has(o.id)}
                        sx={({ palette, functions: { rgba } }) => {
                          const pay = payTypeOf(o.pay_type);
                          if (!pay) return {};

                          return {
                            backgroundColor: rgba(palette[pay.tone].main, 0.08),
                            "&:hover": { backgroundColor: rgba(palette[pay.tone].main, 0.14) },
                          };
                        }}
                      >
                        <TableCell align="center" padding="checkbox">
                          <Checkbox checked={selected.has(o.id)} onChange={() => toggleRow(o.id)} />
                        </TableCell>
                        <IdCell value={o.id} />
                        <TableCell align="center" sx={cell}>
                          {o.code ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                          {o.shop_code ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.customer ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.date ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.goods ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.container ?? "-"}
                        </TableCell>

                        <TableCell align="center" sx={cell}>
                          {o.status ? (
                            <Chip
                              label={o.status}
                              size="small"
                              sx={({ palette, functions: { rgba } }) => ({
                                fontWeight: 700,
                                backgroundColor: rgba(palette.primary.main, 0.16),
                                color: palette.dark.main,
                              })}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>

                        <TableCell align="center" sx={cell}>
                          {money(o.charged)}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {money(o.deposit)}
                        </TableCell>
                        {/* what was paid on top of the deposit, ours to the
                            supplier included. `paid` is the customer's side
                            alone, which is what BALANCE is measured against
                            - a server that does not send `payment` yet has
                            only that to show. */}
                        <TableCell align="center" sx={cell}>
                          {money(o.payment ?? o.paid)}
                        </TableCell>

                        {/* the supplier column is the sum of these, so the
                            two screens net off the same thing - see
                            Supplier::scopeWithBalances */}
                        <TableCell align="center" sx={{ ...cell, opacity: 0.85 }}>
                          {money(expectedBalance(o))}
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={({ palette }) => ({
                            ...cell,
                            fontWeight: 700,
                            // a negative balance means the customer overpaid
                            color: Number(o.balance) < 0 ? palette.error.main : palette.dark.main,
                          })}
                        >
                          {money(o.balance)}
                        </TableCell>

                        <TableCell align="center" sx={cell}>
                          <SuiBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.75,
                            }}
                          >
                            {/* attaching it marks the order DELIVERD, so it
                                belongs where the other row actions are */}
                            <OrderPhoto
                              orderId={o.id}
                              photoUrl={o.photo_url}
                              onChanged={fetchData}
                            />
                            <RowActions
                              actions={[
                                boardAction("order", o, fetchData),
                                {
                                  /* the status is not on the create form -
                                     this is where an order is moved on */
                                  icon: "flag",
                                  label: "Change status",
                                  tone: "info",
                                  disabled: busy,
                                  onClick: () => setStatusFor(o),
                                },
                                {
                                  icon: "edit",
                                  label: "Edit order",
                                  onClick: () => setEditingId(o.id),
                                },
                                {
                                  icon: "content_copy",
                                  label: "Duplicate order",
                                  tone: "primary",
                                  disabled: busy,
                                  onClick: () => duplicateOne(o),
                                },
                                {
                                  icon: "delete",
                                  label: "Delete order",
                                  tone: "danger",
                                  disabled: busy,
                                  onClick: () => deleteOne(o),
                                },
                              ]}
                            />
                          </SuiBox>
                        </TableCell>
                      </TableRow>
                    ))}

                    {filteredRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                          <SuiTypography variant="button" color="text">
                            No orders
                          </SuiTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </SuiBox>
              <Pagination {...pager} />
            </>
          )}
        </Card>
      </SuiBox>

      <AddDialog
        open={adding}
        entity="order"
        onClose={() => setAdding(false)}
        onCreated={fetchData}
      />

      {/* one row's status, from the flag button on that row */}
      <OrderStatusDialog
        open={Boolean(statusFor)}
        order={statusFor}
        statuses={statuses}
        onClose={() => setStatusFor(null)}
        onSaved={fetchData}
      />

      {/* same dialog, loaded with an existing order */}
      <AddDialog
        open={Boolean(editingId)}
        entity="order"
        recordId={editingId}
        /* the photo is not a form field - it uploads on its own and sets the
           status, so it cannot wait for Save */
        extra={
          editingId ? (
            <OrderPhoto
              orderId={editingId}
              photoUrl={orders.find((o) => String(o.id) === String(editingId))?.photo_url || null}
              variant="panel"
              onChanged={fetchData}
            />
          ) : null
        }
        onClose={() => setEditingId(null)}
        onCreated={fetchData}
      />

      <Footer />
    </DashboardLayout>
  );
}

Orders.defaultProps = { lockedStatus: "", title: "Orders" };

Orders.propTypes = {
  // pin the page to one status, matched on the status name
  lockedStatus: PropTypes.string,
  title: PropTypes.string,
};

export default Orders;
